// janus-engine/combat/exit-plan.ts — R-118 (Janus lane N5, 2026-10-01): the combat EXIT decided in the browser.
//
// The port of ft_exit_encounter_combat() (was includes/functions/janus/encounter/exit_encounter_combat.php). Pure: the held encounter +
// readers in, the decided exit out. The combat-truth lane (websocket/combat-truth.ts) posts it; combat_truth_commit_inc.php checks the
// target rows (members of THIS encounter's active combat), saves in one transaction and writes the `combat_ended` row. Rules, in PHP's order:
//   1. contingencies — every armed contingency (a status row of an arming item on a resident) whose packed condition reads `combat_ends`
//      FIRES IN THE PAGE (R-128, lane N5b: start-roll.ts planCombatEndContingencies over A3's engine-fired cast — the adapter fills
//      `contingencies`); the endpoint claims and saves those BEFORE any exit write. Unfired contingencies stay armed (owner 2026-07-24).
//      combatEndContingencyCasters() still names the residents holding one (or an unreadable one): their AP / reaction wait for ooc-rest.
//   2. the encounter scalars: in_combat 0, round 0, pointer '', the legendary interlude cleared.
//   3. every RESET TARGET — the non-deleted residents carrying the active combat id (with no active combat id: every non-deleted
//      resident, PHP's own fallback), dead included: creature_initiative 0, weapon_attack 0, head_attack 0, orb_queue null.
//   4. AP / reaction = the OUT-OF-COMBAT economy as of the exit (N1's R-124 rule pieces, janus-engine/economy/out-of-combat.ts —
//      blockingFlags() + etherPin(), shared, not re-derived): aps → max(0, aps_total ?? 3) unless a `no_actions` payload status holds it,
//      reaction → 1 unless `no_reactions` holds it — a HELD column is not written at all (never a stale held value over SQL); a dead row
//      is not rested; an actor whose blocking status the page cannot read — or a contingency caster, whose statuses the fire may still
//      change — is left to the ooc-rest watcher (it rests the actor once the encounter reads out of combat). Deliberate difference from the
//      PHP (unconditional aps = aps_total / reaction = 1), logged in the lane handoff.
//   5. charges, in PHP's write order (last write per pool wins): Orb Slots back to its baseline max(1, min(10, max ?? 3)) → the per-combat
//      refill (R-115, planned by the adapter) → Fury → 0 (unless controlled_fury) / Tactics → 0 → the Ether + Psi pins (the out-of-combat
//      rule; Psi joined it 2026-10-16).
//   6. counterspell watches cleared; the `combat_ended` log row; the membership closed with the reason.
//   7. R-118 exit plan (2026-10-07): every status row on a reset target whose own item or origin item carries `effect_ends_on_combat_end` ENDS
//      (Psion Metamorph Elastic Joints "until the end of the encounter"; the ability lockout rows of
//      `immune_to_ability_after_effect_until_combat_end`). A definition the page does not hold is `missing` (fetched, then re-planned).
import { statusOriginDefinitionId } from '../statuses/status-origin.js';
import { REFILL_BUCKETS, type RefillOp } from '../charges/combat-refill.js';
import { abilityEntryId, ORB_SLOTS_ID, ORB_QUEUE_MAX_SLOTS } from '../charges/charge-adjust.js';
import { blockingFlags, etherPin, psiPin, ETHER_MECHANIC_ID, PSI_MECHANIC_ID } from '../economy/out-of-combat.js';
import { CONTINGENCY_ARMING_IDS } from '../sanguine/sanguine-gate.js';
import type { CombatStartPlan } from './start-roll.js';
import { ABILITY_LOCKOUT_ID } from '../ability/statuses/combat-lockout.js';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);
const upper = (v: unknown): string => String(scalar(v) ?? '').trim().toUpperCase();
const int = (v: unknown): number => { const n = Number(scalar(v) ?? 0); return Number.isFinite(n) ? Math.trunc(n) : 0; };
/** PHP (int) of a stored scalar ('NA' → 0, '3abc' → 3). */
const phpInt = (v: unknown): number => {
  const s = scalar(v);
  if (typeof s === 'number') return Number.isFinite(s) ? Math.trunc(s) : 0;
  const m = /^\s*([+-]?\d+)/.exec(String(s ?? ''));
  return m ? parseInt(m[1]!, 10) : 0;
};
/** PHP empty() of a stored scalar. */
const phpEmpty = (v: unknown): boolean => { const s = scalar(v); return s === undefined || s === null || s === '' || s === 0 || s === '0' || s === false; };

export const COMBAT_EXIT_RULES = 'n5-r118-2026-10-01' as const;
/** ClassMechanic (includes/additions.php). */
export const FURY_MECHANIC_ID = 'CA6567EA-74E1-41CC-9C7C-E8EEA8C0B6D6';
export const TACTICS_MECHANIC_ID = 'BD233002-F6C0-A136-6EE4-C899A9E92856';
/** FT_ORB_QUEUE_DEFAULT_SLOTS (orb_queue.php). */
export const ORB_QUEUE_DEFAULT_SLOTS = 3;

export interface ExitReaders {
  /** layer_entries() twin (ftLayerEntries). */
  entries: (row: Row | null, key: string) => unknown[];
  /** layer_value() twin (ftLayerValue). */
  value: (row: Row | null, key: string) => unknown;
  /** A catalog definition the page holds, or null. */
  definition: (id: string) => Row | null;
}
export type ExitReason = 'manual' | 'no_opposition';
export interface ExitMemberWrite { id: string; creature_initiative: 0; weapon_attack: 0; head_attack: 0; orb_queue: null; aps?: number; reaction?: number }
export interface CombatExitPlan {
  combat_id: string | null;
  reason: ExitReason;
  encounter: { in_combat: 0; current_round: 0; current_encounter_creature_id_turn: ''; turn_phase: 'main'; pending_main_turn_encounter_creature_id: ''; legendary_turn_queue: '' };
  members: ExitMemberWrite[];
  charges: RefillOp[];
  /** R-128: the combat_ends fires the page decided (start-roll.ts) — the adapter fills it; empty when nobody holds one. */
  contingencies: CombatStartPlan['contingencies'];
  counterspell_clear: true;
  log: 'combat_ended';
  /** R-118 exit plan: the status rows that end with the combat (by row id, on a planned member). */
  status_removals: Array<{ status_id: string; target: string }>;
}
export interface CombatExitDecision {
  plan: CombatExitPlan;
  /** Definition ids the page should fetch and plan again with (each affected actor's AP / reaction was left out). */
  missing: string[];
  /** Actors whose AP / reaction the exit leaves to the ooc-rest watcher, and why. */
  deferred: { actorId: string; reason: string }[];
}

const ecOf = (row: Row): Row => asRow(row.encounter_creatures) ?? {};
const idOf = (row: Row): string => upper(ecOf(row).encounter_creature_id);
function rosterOf(encounter: Row | null): Row[] {
  return Array.isArray(encounter?.encounter_creatures_arr) ? (encounter!.encounter_creatures_arr as unknown[]).map(asRow).filter((r): r is Row => !!r) : [];
}
export function activeCombatIdOf(encounter: Row | null): string {
  const bucket = asRow(encounter?.encounters);
  return upper(bucket && 'active_combat_id' in bucket ? bucket.active_combat_id : encounter?.active_combat_id);
}

/** The exit's reset targets (PHP :90-127): non-deleted residents on the active combat id; no active id → every non-deleted resident. */
export function exitResetTargets(encounter: Row | null): Row[] {
  const active = activeCombatIdOf(encounter);
  return rosterOf(encounter).filter(row => idOf(row) && int(ecOf(row).encounter_creature_deleted) === 0
    && (!active || upper(ecOf(row).combat_id) === active));
}

/** passive_ability_entry_is_active(): no stamp = active; a stamp is judged by truthiness. */
const entryActive = (entry: Row): boolean => !('passive_conditions_active' in entry) || !phpEmpty(entry.passive_conditions_active);

/** ft_reset_orb_slot_capacity(): the FIRST active `passive_ability` entry of the Orb Slots pool → max(1, min(10, max ?? 3)). */
export function orbSlotBaseline(row: Row, readers: ExitReaders): { to: number } | 'missing' | null {
  for (const value of readers.entries(row, 'passive_ability')) {
    const entry = asRow(value);
    if (!entry || !entryActive(entry) || abilityEntryId(entry) !== ORB_SLOTS_ID) continue;
    const charges = asRow(entry.charges);
    let max: unknown = charges && 'max' in charges ? charges.max : 'max_ability_charges' in entry ? entry.max_ability_charges : undefined;
    if (max === undefined) {
      const inline = asRow(entry.item_arr);
      const def = inline && asRow(inline.item_layer_types) ? inline : readers.definition(ORB_SLOTS_ID);
      if (!def) return 'missing';
      const layer = scalar(readers.value(def, 'ability_charges'));
      max = layer === null || layer === undefined ? ORB_QUEUE_DEFAULT_SLOTS : layer;
    }
    return { to: Math.max(1, Math.min(ORB_QUEUE_MAX_SLOTS, phpInt(max))) };
  }
  return null;
}

/** reset_class_mechanics(): a Fury entry (no controlled_fury) or a Tactics entry in any ability bucket → that pool to 0. */
export function classMechanicResets(row: Row, readers: ExitReaders): string[] {
  const special = asRow(row.special_layers);
  const controlledFury = special && 'controlled_fury' in special ? !phpEmpty(special.controlled_fury) : !phpEmpty(readers.value(row, 'controlled_fury'));
  const out: string[] = [];
  for (const bucket of REFILL_BUCKETS) {
    for (const value of readers.entries(row, bucket)) {
      const id = abilityEntryId(value);
      if ((id === FURY_MECHANIC_ID && !controlledFury) || id === TACTICS_MECHANIC_ID) { if (!out.includes(id)) out.push(id); }
    }
  }
  return out;
}

/** The packed contingency condition (ft_contingency_parse_condition): the JSON `{c,…}` shape, else the legacy `cond|mana|level`. */
export function contingencyCondition(packed: unknown): string | null {
  const s = scalar(packed);
  if (typeof s !== 'string' || s === '') return null;
  if (s[0] === '{') { try { const o = JSON.parse(s) as Row; return typeof o?.c === 'string' ? o.c : null; } catch { return null; } }
  return s.split('|')[0] ?? null;
}

/** ft_fire_encounter_contingencies_on_combat_end()'s selection: residents holding an armed contingency that fires at combat end. */
export function combatEndContingencyCasters(encounter: Row | null): string[] {
  const out: string[] = [];
  for (const row of rosterOf(encounter)) {
    if (int(ecOf(row).encounter_creature_deleted) !== 0) continue;
    const statuses = asRow(row.statuses)?.target;
    for (const raw of Array.isArray(statuses) ? statuses : []) {
      const s = asRow(raw);
      if (!s || int(s.status_deleted) !== 0) continue;
      const item = upper(s.status_item_id ?? asRow(asRow(s.status_arr)?.items)?.item_id);
      if (!CONTINGENCY_ARMING_IDS.includes(item)) continue;
      // An unreadable condition is named too: the fire re-reads it on the stored row (an unfired contingency stays armed).
      const cond = contingencyCondition(s.status_casting_targets_arr);
      if (cond !== null && cond !== 'combat_ends') continue;
      const caster = upper(s.status_target_id) || idOf(row);
      if (caster && !out.includes(caster)) out.push(caster);
    }
  }
  return out;
}

/** effect_ends_on_combat_end (R-118 exit plan): the item that ends a status row at combat end — its own item, or the item that applied it (a rider). */
export const COMBAT_END_LAYER = 'effect_ends_on_combat_end';
/** R-118 exit plan: the status rows on one reset target that end with the combat. `missing` = definition ids this page does not hold. */
export function combatEndStatusRemovals(row: Row, readers: ExitReaders): { removals: Array<{ status_id: string; target: string }>; missing: string[] } {
  const target = idOf(row);
  const removals: Array<{ status_id: string; target: string }> = [];
  const missing: string[] = [];
  const statuses = asRow(row.statuses)?.target;
  for (const raw of Array.isArray(statuses) ? statuses : []) {
    const s = asRow(raw);
    if (!s || int(s.status_deleted) !== 0) continue;
    const statusId = upper(s.status_id);
    if (!/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(statusId) || !target) continue;
    const inline = asRow(s.status_arr);
    const item = upper(s.status_item_id ?? asRow(inline?.items)?.item_id);
    // a lockout row (R-172) always ends here — never left to a definition fetch that might time out (its bar would outlive the combat)
    let ends = item === ABILITY_LOCKOUT_ID;
    const origin = statusOriginDefinitionId(s);
    for (const id of ends ? [] : [...new Set([item, origin])].filter(Boolean)) {
      const def = readers.definition(id) ?? (id === item && inline && asRow(inline.item_layer_types) && Object.keys(inline.item_layer_types as Row).length ? inline : null);
      if (!def) { missing.push(id); continue; }
      if (phpEmpty(readers.value(def, COMBAT_END_LAYER))) continue;
      ends = true;
      break;
    }
    if (ends) removals.push({ status_id: statusId, target });
  }
  return { removals, missing };
}

/** The whole exit, decided. `refill` = the R-115 ops the adapter planned for the members (the charge ops compose in PHP's order). */
export function planCombatExit(encounter: Row | null, readers: ExitReaders, input: { reason: ExitReason; refill?: RefillOp[] }): CombatExitDecision {
  const targets = exitResetTargets(encounter);
  const roster = rosterOf(encounter);
  const contingencyCasters = combatEndContingencyCasters(encounter);
  const missing = new Set<string>();
  const deferred: CombatExitDecision['deferred'] = [];
  // The out-of-combat economy as of the exit: N1's rule pieces (the readers in its shape; the encounter catalog as its fallback store).
  const economyReaders = { layerValue: readers.value, entries: readers.entries, definition: readers.definition };
  const catalog = asRow(encounter?.catalog);

  const members: ExitMemberWrite[] = [];
  const orbOps: RefillOp[] = [], classOps: RefillOp[] = [], etherOps: RefillOp[] = [];
  const statusRemovals: CombatExitPlan['status_removals'] = [];
  for (const row of targets) {
    const id = idOf(row);
    const ec = ecOf(row);
    const write: ExitMemberWrite = { id, creature_initiative: 0, weapon_attack: 0, head_attack: 0, orb_queue: null };
    const dead = int(ec.encounter_creature_dead) !== 0;
    if (dead) deferred.push({ actorId: id, reason: 'dead' });
    else if (contingencyCasters.includes(id)) deferred.push({ actorId: id, reason: 'contingency-caster' });
    else {
      const flags = blockingFlags(row, roster, economyReaders, asRow(row.catalog) ?? catalog);
      if (flags.unknown.length) {
        for (const u of flags.unknown) if (!u.startsWith('aura-owner:')) missing.add(u);
        deferred.push({ actorId: id, reason: 'status-unknown:' + flags.unknown.join(',') });
      } else {
        if (!flags.noActions) write.aps = Math.max(0, ec.aps_total === undefined || ec.aps_total === null ? 3 : int(ec.aps_total));
        if (!flags.noReactions) write.reaction = 1;
      }
    }
    if (!dead) {
      const ether = etherPin(row, economyReaders);
      if (ether === 'missing') missing.add(ETHER_MECHANIC_ID);
      else if (ether) etherOps.push({ holder: id, item_id: ether.itemId, to: ether.after });
      const psi = psiPin(row, economyReaders);
      if (psi === 'missing') missing.add(PSI_MECHANIC_ID);
      else if (psi) etherOps.push({ holder: id, item_id: psi.itemId, to: psi.after });
    }
    members.push(write);
    const orb = orbSlotBaseline(row, readers);
    if (orb === 'missing') missing.add(ORB_SLOTS_ID);
    else if (orb) orbOps.push({ holder: id, item_id: ORB_SLOTS_ID, to: orb.to });
    for (const mech of classMechanicResets(row, readers)) classOps.push({ holder: id, item_id: mech, to: 0 });
    const ending = combatEndStatusRemovals(row, readers);
    for (const m of ending.missing) missing.add(m);
    statusRemovals.push(...ending.removals);
  }
  // PHP's write order per pool: orb baseline (the member loop) → the refill → class mechanics → (the out-of-combat Ether / Psi pins).
  const memberIds = new Set(members.map(m => m.id));
  const charges = new Map<string, RefillOp>();
  for (const op of [...orbOps, ...(input.refill ?? []).filter(op => memberIds.has(upper(op.holder))), ...classOps, ...etherOps]) {
    const key = upper(op.holder) + ':' + upper(op.item_id);
    charges.delete(key);
    charges.set(key, { holder: upper(op.holder), item_id: upper(op.item_id), to: Math.max(0, Math.trunc(op.to)) });
  }
  const combatId = activeCombatIdOf(encounter);
  return {
    plan: {
      combat_id: combatId || null, reason: input.reason,
      encounter: { in_combat: 0, current_round: 0, current_encounter_creature_id_turn: '', turn_phase: 'main', pending_main_turn_encounter_creature_id: '', legendary_turn_queue: '' },
      members, charges: [...charges.values()], contingencies: { steps: [], skipped: [] }, counterspell_clear: true, log: 'combat_ended',
      status_removals: statusRemovals,
    },
    missing: [...missing].filter(Boolean),
    deferred,
  };
}
